import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/AuthService';
import { useAuth } from '../context/AuthContext';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import {
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Typography,
    Fade,
    Alert
} from '@mui/material';
import {
    AccountCircle,
    Lock,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import LoadingSpinner from "../utils/LoadingSpinner";

const LoginPage = () => {
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorVisible, setErrorVisible] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth();

    useEffect(() => {
        if (error) {
            setTimeout(() => {
                setErrorVisible(true);
            }, 100);
        } else {
            setErrorVisible(false);
        }
    }, [error]);

    const isFormValid = username.trim() !== '' && password.trim() !== '';

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!isFormValid) {
            setError('Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);
        setError(null);
        setErrorVisible(false);

        try {
            const data = await AuthService.login(username, password);
            login(data);
            navigate('/');
        } catch (error) {
            setError('Nom d\'utilisateur ou mot de passe incorrect');
        } finally {
            setLoading(false);
        }
    };

    const hideError = () => {
        setErrorVisible(false);
        setTimeout(() => {
            setError(null);
        }, 300);
    };

    return (
        <>
            <LoadingSpinner
                isVisible={isLoading}
                loadingText="Chargement de la page..."
            />

            <div style={{
                backgroundImage: `url('assets/images/banner/bg-register.jpg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    padding: '20px',
                    borderRadius: '10px',
                    width: '100%',
                    maxWidth: '500px',
                    margin: '20px auto',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease'
                }}>
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Link to="/">
                            <img
                                src='/assets/images/logo/logo.png'
                                alt="Logo"
                                style={{
                                    width: '100px',
                                    height: '100px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '3px solid rgb(23 50 71)'
                                }}
                                onError={(e) => {
                                    e.target.src = '/assets/images/logo/1.png';
                                }}
                            />
                        </Link>
                    </div>
                    <Typography variant="h4" style={{ textAlign: 'center', marginBottom: '20px' }}>
                        Connexion
                    </Typography>

                    <form onSubmit={handleLogin} style={{ marginTop: 10 }} autoComplete="off">
                        <Fade in timeout={1000}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <TextField
                                    label="Nom d'utilisateur"
                                    name="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value.toLowerCase())}
                                    disabled={loading}
                                    error={!username.trim() && username !== ''}
                                    helperText={!username.trim() && username !== '' ? "Ce champ est requis" : ""}
                                    autoComplete="off"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AccountCircle />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <TextField
                                    label="Mot de passe"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={loading}
                                    error={!password.trim() && password !== ''}
                                    helperText={!password.trim() && password !== '' ? "Ce champ est requis" : ""}
                                    autoComplete="new-password"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => !loading && setShowPassword(!showPassword)}
                                                    edge="end"
                                                    disabled={loading}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {error && (
                                    <Alert
                                        severity="error"
                                        style={{
                                            marginTop: '20px',
                                            opacity: errorVisible ? 1 : 0,
                                            transition: 'opacity 0.3s ease-in-out'
                                        }}
                                        action={
                                            <IconButton
                                                aria-label="close"
                                                color="inherit"
                                                size="small"
                                                onClick={hideError}
                                            >
                                                <FaTimes />
                                            </IconButton>
                                        }
                                    >
                                        {error}
                                    </Alert>
                                )}

                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '20px',
                                    padding: '0 10px'
                                }}>
                                    <Link
                                        to="/forgot-username"
                                        style={{
                                            color: '#1d8d36',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Identifiant oublié ?
                                    </Link>
                                    <Link
                                        to="/forgot-password"
                                        style={{
                                            color: '#1d8d36',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}
                                    >
                                        Mot de passe oublié ?
                                    </Link>
                                </div>

                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={loading || !isFormValid}
                                        style={{
                                            backgroundColor: (loading || !isFormValid) ? 'rgba(29,141,54,0.58)' : '#1d8d36',
                                            minWidth: '150px',
                                            transition: 'background-color 0.3s ease',
                                        }}
                                    >
                                        {loading ? (
                                            <>
                                                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '18px' }} />
                                                <span style={{ marginLeft: '8px' }}>Chargement...</span>
                                            </>
                                        ) : 'Se connecter'}
                                    </Button>
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    Vous n'avez pas encore un compte ? <Link to="/register">Créer un compte</Link>
                                </div>
                            </div>
                        </Fade>
                    </form>
                </div>

                <style jsx>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </>
    );
};

export default LoginPage;